import * as Sentry from "@sentry/react";
import { resolveWebEnvironment } from "@neumind/shared/analytics";

/**
 * Initialize Sentry for www site error tracking
 * Uses single "www" project with environment tagging
 * (development/staging/production/unknown — ADR 0012 vocabulary).
 *
 * ISS-260807-tz8k: this used to read `import.meta.env.MODE` directly. Vite
 * silently defaults MODE to "production" for any `vite build` run without
 * `--mode` (e.g. website-preview.yml before its fix, or a bare local build),
 * which mislabelled those builds as production. `resolveWebEnvironment` is
 * the one derivation point for this surface — the PostHog environment
 * super-property (sites/www/src/components/widgets/PostHogProvider.tsx)
 * routes through the same function, so Sentry and PostHog can never
 * disagree about which environment a given www build reports.
 */
export function initializeSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN_WWW;
  const environment = resolveWebEnvironment(
    import.meta.env.VITE_ENVIRONMENT,
    import.meta.env.MODE
  );

  // Skip Sentry in local development to avoid polluting error tracking
  if (environment === 'development') {
    console.log('[Sentry] Skipping initialization in development environment');
    return;
  }

  if (!dsn) {
    console.warn("[Sentry] VITE_SENTRY_DSN_WWW not configured, skipping initialization");
    return;
  }

  Sentry.init({
    dsn,
    environment,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance traces sample rate
    tracesSampleRate: environment === "production" ? 0.1 : 1.0, // 10% in prod, 100% in dev/staging

    // Session Replay sample rate
    replaysSessionSampleRate: environment === "production" ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0, // Always capture replays for errors

    // Error filtering
    beforeSend(event, hint) {
      // Don't send certain errors to Sentry
      const error = hint.originalException;

      // Filter out network errors (contact form submissions, etc.)
      if (error instanceof Error && error.message.includes("Network Error")) {
        return null;
      }

      return event;
    },
  });

  console.log(`[Sentry] Initialized for environment: ${environment}`);
}
